import React from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getChannelIconElement, type ChannelIconSize } from '@/lib/channel-utils';
import type { Channel } from '@/services/business/calendar';

type PillSize = 'sm' | 'md' | 'lg';
type PillVariant = 'default' | 'elevated' | 'overlay';
type DateFormat = 'none' | 'short' | 'full';

interface PublicationDatePillProps {
  time: string;
  date?: Date;
  channels: Channel[];
  maxChannels?: number;
  size?: PillSize;
  dateFormat?: DateFormat;
  variant?: PillVariant;
  descheduled?: boolean;
  fullWidth?: boolean;
  className?: string;
}

const SIZE_CFG: Record<PillSize, { text: string; icon: ChannelIconSize; gap: string; pad: string; clock: string }> = {
  sm: { text: 'text-[10px]', icon: 'xs', gap: 'gap-1', pad: 'px-1.5 py-0.5', clock: 'w-3 h-3' },
  md: { text: 'text-[11px]', icon: 'md', gap: 'gap-1.5', pad: 'px-2.5 py-1.5', clock: 'w-3 h-3' },
  lg: { text: 'text-xs', icon: 'md', gap: 'gap-2', pad: 'px-2.5 py-1', clock: 'w-4 h-4' },
};

const VARIANT_CFG: Record<PillVariant, string> = {
  default: 'bg-white/[0.06] text-white/70',
  elevated: 'bg-white/[0.08] text-white/70',
  overlay: 'bg-black/60 text-white/80 backdrop-blur-sm',
};

export const PublicationDatePill: React.FC<PublicationDatePillProps> = ({
  time,
  date,
  channels,
  maxChannels = 3,
  size = 'md',
  dateFormat = 'none',
  variant = 'default',
  descheduled = false,
  fullWidth = false,
  className,
}) => {
  const cfg = SIZE_CFG[size];

  const formattedDate = date && dateFormat !== 'none'
    ? dateFormat === 'full'
      ? format(date, "EEE d MMM", { locale: fr })
      : format(date, 'd MMM', { locale: fr })
    : null;

  const visibleChannels = channels.slice(0, maxChannels);
  const overflowCount = channels.length - maxChannels;

  return (
    <span
      className={cn(
        'group/pill inline-flex items-center rounded-full font-medium w-fit',
        cfg.pad,
        cfg.text,
        VARIANT_CFG[variant],
        descheduled && 'border border-dashed border-[hsl(var(--v10-border))] opacity-60',
        fullWidth && 'w-full justify-between',
        className,
      )}
    >
      {/* Clock + Time */}
      <span className="inline-flex items-center">
        {time && (
          <>
            <Clock className={cn(
              cfg.clock,
              'shrink-0 mr-1.5',
              descheduled ? 'text-white/30' : 'text-[#999999]',
            )} />
            <span className={cn(
              descheduled && 'text-white/40 line-through',
            )}>
              {time}
            </span>
          </>
        )}
        {formattedDate && (
          <span className="opacity-60 ml-1">{formattedDate}</span>
        )}
      </span>

      {/* Channel icons */}
      {visibleChannels.length > 0 && (
        <span className={cn(
          'inline-flex items-center gap-1 ml-3',
          descheduled ? 'text-white/20' : 'text-white/40 group-hover/pill:text-white/60',
        )}>
          {visibleChannels.map((ch) => (
            <span
              key={ch}
              className={cn(
                'transition-all duration-150',
                !descheduled && 'hover:text-white hover:scale-110',
              )}
            >
              {getChannelIconElement(ch, cfg.icon)}
            </span>
          ))}
          {overflowCount > 0 && (
            <span className="text-[10px]">+{overflowCount}</span>
          )}
        </span>
      )}
    </span>
  );
};
